import {
  type BattleSummary,
  battleRoundsLabel,
  defaultBattleCopy,
  defaultBattlesLobbyCopy,
} from "@loot-os/api-contract";
import type { BattleRoundBox } from "@loot-os/design-system/domain/battles/battleTypes";
import type { BattleCardContent } from "@loot-os/design-system/templates/battles/battlesContent";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { battleVisualStatus } from "../../../../lib/battleTimeline.js";
import { useProfile } from "../../../../lib/profile.js";
import { boxesQueryOptions } from "../../../../lib/queries.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { battleTags, roundBoxes, toSeatView } from "./battleViews.js";

/**
 * Turns a battle from GET /v1/battles into its lobby card, the same wherever one shows (the lobby,
 * Home): the match-up, tags, price, the boxes by round (named from GET /v1/boxes), the seats, and
 * the one way in. The shopper's end-user id is not on the BFF session and the summaries carry no
 * `me` block, so a card cannot name "You": every seat shows its display name.
 */
export function useBattleCard(): (summary: BattleSummary) => BattleCardContent {
  const terms = useTerminology();
  const { showProfile } = useProfile();
  const boxes = useQuery(boxesQueryOptions());
  const boxMap = useMemo(() => {
    const map = new Map<string, BattleRoundBox>();
    for (const b of boxes.data ?? []) map.set(b.id, { name: b.name, imageUrl: b.imageUrl });
    return map;
  }, [boxes.data]);
  const words = defaultBattleCopy(terms);
  const copy = defaultBattlesLobbyCopy(terms);
  return (summary) => {
    const status = battleVisualStatus(summary);
    const filled = summary.seats.filter((s) => s.kind !== "empty").length;
    return {
      id: summary.id,
      title: copy.list.matchup(summary.playerCount),
      tags: battleTags(summary, words),
      priceCents: summary.priceCents,
      rounds: roundBoxes(summary.rounds, boxMap, words.unknownBox),
      roundsSummary: battleRoundsLabel(summary.roundCount),
      progress:
        status === "in-progress"
          ? words.roundOf(Math.max(0, summary.currentRevealedRound) + 1, summary.roundCount)
          : undefined,
      seats: summary.seats.map((seat) => toSeatView(seat, undefined, showProfile, words)),
      seatsSummary: copy.list.seatsFilled(filled, summary.playerCount),
      action: {
        label: copy.list.actions[summary.status],
        href: `/battles/${summary.id}`,
        emphasis: status === "joinable" ? "primary" : "secondary",
      },
      finished: status === "finished",
      currentRound:
        status === "in-progress" ? Math.max(0, summary.currentRevealedRound) : undefined,
      teamSize: summary.teamCount ? summary.playerCount / summary.teamCount : 1,
      roundLabel: words.roundBox,
    };
  };
}
